import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Link2, Loader2, ImageOff, Sparkles } from "lucide-react";
import { api, formatApiError, safeImg } from "@/lib/api";
import { inputCls, btnPrimary, btnSecondary, Field } from "@/components/shared";

const LAST_KEY = "bb-last-wishlist";
export const extractUrl = (s) => (s || "").match(/https?:\/\/[^\s<>"']+/i)?.[0] || "";

const Thumb = ({ src }) => {
  const [broken, setBroken] = useState(false);
  useEffect(() => setBroken(false), [src]);
  return (
    <div className="h-28 w-28 shrink-0 rounded-xl overflow-hidden bg-t-surface border border-t-line flex items-center justify-center" data-testid="link-preview-image">
      {src && !broken ? <img src={safeImg(src)} alt="" className="h-full w-full object-cover" onError={() => setBroken(true)} /> : <ImageOff className="h-6 w-6 text-t-faint" />}
    </div>
  );
};

export const LinkItemForm = ({ initialUrl = "", initialName = "", onDone, onCancel }) => {
  const [url, setUrl] = useState(initialUrl);
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);
  const [form, setForm] = useState({ name: initialName, price: "", image_url: "", notes: "", site: "" });
  const [wishlists, setWishlists] = useState([]);
  const [listId, setListId] = useState("");
  const auto = useRef(false);

  useEffect(() => {
    api.get("/shop-lists").then((r) => {
      const w = r.data.filter((l) => l.kind === "wishlist");
      setWishlists(w);
      const last = localStorage.getItem(LAST_KEY);
      setListId(w.find((l) => l.id === last)?.id || w[0]?.id || "");
    });
  }, []);

  const lookup = async (target = url) => {
    const link = target.trim();
    if (!link) return;
    setLoading(true);
    try {
      const { data } = await api.post("/link-preview", { url: link });
      setUrl(data.url);
      setForm((f) => ({ ...f, name: data.title || f.name, price: data.price ?? f.price, image_url: data.image || f.image_url, site: data.site }));
      if (!data.title && !data.image) toast("This shop didn't share its details, so add them by hand");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
      setFetched(true);
    }
  };

  useEffect(() => {
    if (initialUrl && !auto.current) {
      auto.current = true;
      lookup(initialUrl);
    }
  }, [initialUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Give the item a name");
    let target = listId;
    if (!target) target = (await api.post("/shop-lists", { name: "My Wishlist", kind: "wishlist" })).data.id;
    const body = { name: form.name.trim(), price: form.price === "" ? null : Number(form.price), url: url.trim() || null, image_url: form.image_url || null, notes: form.notes || null };
    const r = await api.post(`/shop-lists/${target}/items`, body);
    localStorage.setItem(LAST_KEY, target);
    toast.success(`Added to ${r.data.name}`);
    onDone?.(r.data);
  };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <form onSubmit={save} className="space-y-5" data-testid="link-item-form">
      <Field label="Link">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Link2 className="h-4 w-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-t-faint" />
            <input data-testid="link-url-input" className={`${inputCls} pl-10`} value={url} placeholder="Paste a link from any shop"
              onChange={(e) => setUrl(e.target.value)} onPaste={(e) => { const t = extractUrl(e.clipboardData.getData("text")); if (t) { e.preventDefault(); setUrl(t); lookup(t); } }} />
          </div>
          <button type="button" data-testid="link-fetch-button" onClick={() => lookup()} disabled={loading || !url.trim()} className={btnSecondary}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Get details
          </button>
        </div>
      </Field>
      {(fetched || initialName) && (
        <div className="flex gap-4 fade-up" data-testid="link-preview">
          <Thumb src={form.image_url} />
          <div className="flex-1 space-y-3 min-w-0">
            {form.site && <p className="text-xs uppercase tracking-[0.2em] text-t-muted" data-testid="link-preview-site">{form.site}</p>}
            <input data-testid="link-name-input" className={inputCls} value={form.name} onChange={set("name")} placeholder="Item name" />
            <input data-testid="link-price-input" type="number" step="0.01" min="0" className={inputCls} value={form.price} onChange={set("price")} placeholder="Price in AUD (optional)" />
          </div>
        </div>
      )}
      <Field label="Add to wishlist">
        {wishlists.length ? (
          <select data-testid="link-wishlist-select" className={inputCls} value={listId} onChange={(e) => setListId(e.target.value)}>
            {wishlists.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        ) : (
          <p className="text-sm text-t-muted" data-testid="link-new-wishlist-note">We'll create "My Wishlist" for you.</p>
        )}
      </Field>
      <div className="flex gap-3">
        {onCancel && <button type="button" onClick={onCancel} data-testid="link-cancel-button" className={btnSecondary}>Cancel</button>}
        <button type="submit" data-testid="link-save-button" disabled={loading} className={`${btnPrimary} flex-1`}>Add to wishlist</button>
      </div>
    </form>
  );
};
